/* eslint-disable react/require-default-props */
import React, {useRef, useState} from 'react';
import {View} from 'react-native';
import {get, noop} from 'lodash';

import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import {AccessibilityWrapper} from '/component/layout/AccessibilityWrapper';
import {Shared} from '/constant/TestIDs';
import {withTranslation} from '/provider/TranslationProvider';
import {
  generateCallback,
  generateComponentCallback,
} from '/util/component.util';
import {getDateUtilDefaultInstance} from '/util/date.util';

import {hitSlopIndent, styles} from './Timestamp.style';
import {refreshText} from './Timestamp.util';
import {useSharedRefresher} from './useSharedRefresher';

// Define an interface for the component props
interface TimestampProps {
  tt: (keyPrefix: any, value: any) => void;
  style: object;
  refreshTime: Date | null;
  onShowInfoPopover?: (payload: {
    triggerRef: React.RefObject<any>;
    [key: string]: any;
  }) => void;
  infoButtonLabel: string;
}

export const refreshTextString = ({
  minSinceRefresh,
}: {
  minSinceRefresh: number;
}) => {
  if (minSinceRefresh < 1) return 'just now';
  return `${minSinceRefresh} minutes ago`;
};

export const Timestamp: React.FC<TimestampProps> = ({
  tt = withTranslation.ttDefaultProp,
  style: styleProp = {},
  refreshTime = null,
  onShowInfoPopover = noop,
  infoButtonLabel,
}: TimestampProps) => {
  const [isFocused] = useState(false);
  const infoButtonRef = useRef(null);

  const minSinceRefresh =
    getDateUtilDefaultInstance().getDifference(refreshTime);

  useSharedRefresher();

  if (!refreshTime) {
    return null;
  }

  const hitSlop = {
    top: hitSlopIndent,
    bottom: hitSlopIndent,
    left: hitSlopIndent,
    right: hitSlopIndent,
  };

  const focusedStyle = {
    borderWidth: 2,
    borderColor: '#005fcc',
    borderRadius: 4,
  };

  const handleInfoPress = generateCallback({
    func: (payload?: any) => {
      const popoverPayload = {
        triggerRef: infoButtonRef,
        ...payload,
      };
      onShowInfoPopover?.(popoverPayload);
    },
  });

  return (
    <AccessibilityWrapper accessible={false}>
      <View
        style={[styles.container, get(styleProp, 'base')]}
        accessible={false}>
        <KeyboardFocusable style={isFocused ? focusedStyle : undefined}>
          <Text
            testID={Shared.sharedTimestamp}
            allowFontScaling
            maxFontSizeMultiplier={2}
            accessible={true}
            accessibilityLabel={`Data last refreshed: ${refreshTextString({
              minSinceRefresh,
              tt,
            })}`}>
            <Text
              variant="caption"
              style={styles.text}
              allowFontScaling
              maxFontSizeMultiplier={2}
              accessible={true}>
              {tt(
                'heading',
                generateComponentCallback({
                  component: <>Data last refreshed: </>,
                }),
              )}
            </Text>
            <Text
              variant="caption"
              style={[styles.text, styles.time]}
              allowFontScaling
              maxFontSizeMultiplier={2}
              accessible={false}>
              {refreshText({minSinceRefresh, tt})}
            </Text>
          </Text>
        </KeyboardFocusable>
        <KeyboardFocusable
          viewRef={infoButtonRef}
          onPress={handleInfoPress}
          accessible={true}
          focusable={true}
          accessibilityRole="button"
          accessibilityLabel={infoButtonLabel || 'Show data refresh info'}
          hitSlop={hitSlop}
          focusRingStyle={styles.infoButtonFocusRing}>
          <Icon name="info" style={styles.infoIcon} />
        </KeyboardFocusable>
      </View>
    </AccessibilityWrapper>
  );
};

export default withTranslation('timestamp')(Timestamp);
